import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import MainStructure from '../../components/general/MainStructure';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import * as Icon from 'react-feather';
import { format } from 'date-fns';

function ApiAccess() {
  const { user } = useAuth();

  // ── API Keys ──────────────────────────────────────────────────────────────
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Webhook ───────────────────────────────────────────────────────────────
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [webhookSaved, setWebhookSaved] = useState(false);

  // ── OAuth Apps ────────────────────────────────────────────────────────────
  const [oauthApps, setOauthApps] = useState([]);
  const [oauthTokens, setOauthTokens] = useState([]);
  const [oauthLoading, setOauthLoading] = useState(true);
  const [newAppName, setNewAppName] = useState('');
  const [newAppRedirectUri, setNewAppRedirectUri] = useState('');
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [createdApp, setCreatedApp] = useState(null); // { client_id, client_secret } — shown once
  const [copiedCid, setCopiedCid] = useState(false);
  const [copiedCs, setCopiedCs] = useState(false);
  const [setupApp, setSetupApp] = useState(null); // app whose setup instructions are open
  const [copiedAuthUrl, setCopiedAuthUrl] = useState(false);

  const BASE_URL = 'https://api.schedpilot.com/wp-json/schedpilot/v1';

  useEffect(() => {
    fetchKeys();
    fetchWebhookSettings();
    fetchOauthApps();
    fetchOauthTokens();
  }, []);

  // ── API Keys helpers ──────────────────────────────────────────────────────

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api-keys`, { credentials: 'include' });
      const data = await res.json();
      if (data.code === 200) setKeys(data.keys);
    } catch (err) {
      console.error('Failed to fetch keys', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookSettings = async () => {
    try {
      const res = await fetch(`${BASE_URL}/get-webhook`, { credentials: 'include' });
      const data = await res.json();
      if (data.code === 200) {
        setWebhookUrl(data.webhook_url);
        setWebhookSecret(data.webhook_secret);
      }
    } catch (err) {
      console.error('Failed to fetch webhook settings', err);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const res = await fetch(`${BASE_URL}/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newKeyLabel || 'My API Key' }),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.code === 201) {
        setGeneratedKey(data.plain_key);
        setNewKeyLabel('');
        setCopied(false);
        fetchKeys();
      }
    } catch (err) {
      alert('Error creating key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveWebhook = async () => {
    setIsSavingWebhook(true);
    try {
      const res = await fetch(`${BASE_URL}/update-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: webhookUrl }),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.code === 200) {
        setWebhookSaved(true);
        setTimeout(() => setWebhookSaved(false), 3000);
      } else {
        alert(data.message || 'Error saving webhook');
      }
    } catch (err) {
      alert('Network error saving webhook');
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure? This will immediately break any scripts using this key.')) return;
    try {
      const res = await fetch(`${BASE_URL}/api-keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) fetchKeys();
    } catch (err) {
      console.error('Failed to revoke', err);
    }
  };

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  // ── OAuth helpers ─────────────────────────────────────────────────────────

  const fetchOauthApps = async () => {
    try {
      const res = await fetch(`${BASE_URL}/oauth-apps`, { credentials: 'include' });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.apps || []);
      setOauthApps(list);
    } catch (err) {
      console.error('Failed to fetch OAuth apps', err);
    } finally {
      setOauthLoading(false);
    }
  };

  const fetchOauthTokens = async () => {
    try {
      const res = await fetch(`${BASE_URL}/oauth-tokens`, { credentials: 'include' });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.tokens || []);
      setOauthTokens(list);
    } catch (err) {
      console.error('Failed to fetch OAuth tokens', err);
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    setIsCreatingApp(true);
    try {
      const res = await fetch(`${BASE_URL}/oauth-apps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newAppName, redirect_uri: newAppRedirectUri }),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.client_id) {
        setCreatedApp({ client_id: data.client_id, client_secret: data.client_secret });
        setCopiedCid(false);
        setCopiedCs(false);
        setNewAppName('');
        setNewAppRedirectUri('');
        fetchOauthApps();
      } else {
        alert(data.message || 'Error creating OAuth app');
      }
    } catch (err) {
      alert('Error creating OAuth app');
    } finally {
      setIsCreatingApp(false);
    }
  };

  const handleDeleteApp = async (id) => {
    if (!window.confirm('Delete this app? All active tokens issued to it will be revoked immediately.')) return;
    try {
      await fetch(`${BASE_URL}/oauth-apps/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchOauthApps();
      fetchOauthTokens();
    } catch (err) {
      console.error('Failed to delete app', err);
    }
  };

  const handleRevokeToken = async (id) => {
    if (!window.confirm('Revoke this token? Any agent using it will lose access immediately.')) return;
    try {
      await fetch(`${BASE_URL}/oauth-tokens/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchOauthTokens();
    } catch (err) {
      console.error('Failed to revoke token', err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <MainStructure activeItem={'api-access'}>
      <div className="container-fluid p-4">

        {/* Header */}
        <div className="row mb-4">
          <div className="col-md-8">
            <h1 className="h3 mb-1" style={{ fontWeight: 800 }}>API Access</h1>
            <p className="text-muted">Generate keys or register OAuth apps to post programmatically to your connected social accounts.</p>
          </div>
          <div className="col-md-4 text-md-end">
            <a href="https://docs.schedpilot.com" target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm me-2">
              <Icon.Book size={16} className="me-1" /> Documentation
            </a>
          </div>
        </div>

        {/* ── Personal API Keys ─────────────────────────────────────────────── */}
        <div className="card shadow-sm mb-4 border-01">
          <div className="card-body">
            <h5 className="card-title fw-bold">Generate New API Key</h5>
            <p className="text-muted mb-3">Use API keys for direct integrations (Zapier, custom scripts). Pass as <code>X-API-KEY</code> header.</p>
            <form onSubmit={handleCreateKey} className="row g-3 align-items-center mt-1">
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Key Label (e.g. Cursor IDE, Zapier)"
                  value={newKeyLabel}
                  onChange={(e) => setNewKeyLabel(e.target.value)}
                  required
                />
              </div>
              <div className="col-auto">
                <button type="submit" className="btn btn-primary px-4" disabled={isCreating}>
                  {isCreating ? 'Generating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-01 mb-5">
          <div className="card-header bg-white py-3 border-bottom">
            <h6 className="mb-0 fw-bold">Active API Keys</h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-3">Label</th>
                  <th>Key Preview</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th className="text-end px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5"><LoadingSpinner /></td></tr>
                ) : keys.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No API keys yet.</td></tr>
                ) : (
                  keys.map(key => (
                    <tr key={key.id}>
                      <td className="fw-bold px-3">{key.label}</td>
                      <td><code>smm_••••••••{key.last_4}</code></td>
                      <td>{format(new Date(key.created_at), 'MMM dd, yyyy')}</td>
                      <td>{key.last_used_at ? format(new Date(key.last_used_at), 'MMM dd, HH:mm') : <span className="text-muted small">Never</span>}</td>
                      <td className="text-end px-4">
                        <button className="btn btn-link btn-sm text-danger" onClick={() => handleRevoke(key.id)}>
                          <Icon.Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── OAuth Apps ────────────────────────────────────────────────────── */}
        <div className="mb-2">
          <h5 className="fw-bold mb-0">OAuth Apps</h5>
          <p className="text-muted mt-1 mb-4">
            Register an OAuth 2.0 app to let AI agents (Claude, OpenClaw, Hermes, etc.) request access to your SchedPilot account
            via the standard Authorization Code flow. Pass tokens as <code>Authorization: Bearer sp_tok_...</code>.
          </p>
        </div>

        {/* Register new app */}
        <div className="card shadow-sm mb-4 border-01">
          <div className="card-body">
            <h6 className="fw-bold mb-3"><Icon.PlusCircle size={16} className="me-2 text-primary"/>Register New OAuth App</h6>
            <form onSubmit={handleCreateApp}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">App Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. My AI Agent"
                    value={newAppName}
                    onChange={(e) => setNewAppName(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Redirect URI</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://myagent.example.com/oauth/callback"
                    value={newAppRedirectUri}
                    onChange={(e) => setNewAppRedirectUri(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button type="submit" className="btn btn-primary w-100" disabled={isCreatingApp}>
                    {isCreatingApp ? 'Creating...' : 'Register'}
                  </button>
                </div>
              </div>
              <p className="text-muted mt-2 mb-0">
                <Icon.Info size={12} className="me-1"/>
                The client secret is shown <strong>once</strong> after registration. Store it securely.
              </p>
            </form>
          </div>
        </div>

        {/* Apps table */}
        <div className="card shadow-sm border-01 mb-5">
          <div className="card-header bg-white py-3 border-bottom">
            <h6 className="mb-0 fw-bold">Registered Apps</h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-3">App Name</th>
                  <th>Client ID</th>
                  <th>Callback URL</th>
                  <th>Active Tokens</th>
                  <th>Created</th>
                  <th className="text-end px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {oauthLoading ? (
                  <tr><td colSpan="6" className="text-center py-5"><LoadingSpinner /></td></tr>
                ) : oauthApps.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-5 text-muted">No OAuth apps registered yet.</td></tr>
                ) : (
                  oauthApps.map(app => (
                    <tr key={app.id}>
                      <td className="fw-bold px-3">{app.name}</td>
                      <td>
                        <code className="text-secondary small">
                          {app.client_id ? `${app.client_id.slice(0, 18)}••••` : '—'}
                        </code>
                      </td>
                      <td className="text-muted small" style={{ maxWidth: 220 }}>
                        {app.redirect_uri ? (
                          <span title={app.redirect_uri} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {app.redirect_uri}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${app.active_tokens > 0 ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                          {app.active_tokens ?? 0}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {app.created_at ? format(new Date(app.created_at), 'MMM dd, yyyy') : '—'}
                      </td>
                      <td className="text-end px-4">
                        <button className="btn btn-link btn-sm text-primary me-1" onClick={() => setSetupApp(app)} title="Setup instructions">
                          <Icon.BookOpen size={16} />
                        </button>
                        <button className="btn btn-link btn-sm text-danger" onClick={() => handleDeleteApp(app.id)} title="Delete app">
                          <Icon.Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Active OAuth Tokens */}
        <div className="card shadow-sm border-01 mb-5">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold">Active OAuth Tokens</h6>
            <button className="btn btn-link btn-sm p-0 text-muted" onClick={fetchOauthTokens} title="Refresh">
              <Icon.RefreshCw size={14} />
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-3">App</th>
                  <th>Token Preview</th>
                  <th>Issued</th>
                  <th>Last Used</th>
                  <th className="text-end px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {oauthTokens.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No active OAuth tokens.</td></tr>
                ) : (
                  oauthTokens.map(tok => (
                    <tr key={tok.id}>
                      <td className="fw-bold px-3">{tok.app_name || '—'}</td>
                      <td><code className="text-secondary small">sp_tok_••••••••{tok.last_4 || '????'}</code></td>
                      <td className="text-muted small">{tok.created_at ? format(new Date(tok.created_at), 'MMM dd, yyyy') : '—'}</td>
                      <td className="text-muted small">{tok.last_used_at ? format(new Date(tok.last_used_at), 'MMM dd, HH:mm') : <span className="text-muted">Never</span>}</td>
                      <td className="text-end px-4">
                        <button className="btn btn-link btn-sm text-danger" onClick={() => handleRevokeToken(tok.id)} title="Revoke token">
                          <Icon.XCircle size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Webhook Settings ──────────────────────────────────────────────── */}
        <div className="card shadow-sm border-01 mt-2 mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-2"><Icon.Activity size={18} className="me-2 text-primary"/>Webhook Settings</h5>
            <p className="text-muted mb-4">Receive real-time notifications when your posts are published or fail.</p>
            <div className="row g-3 align-items-center">
              <div className="col-md-8">
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://your-server.com/webhooks/schedpilot"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              <div className="col-auto">
                <button
                  className={`btn px-4 ${webhookSaved ? 'btn-success' : 'btn-outline-primary'}`}
                  onClick={handleSaveWebhook}
                  disabled={isSavingWebhook}
                >
                  {isSavingWebhook ? 'Saving...' : webhookSaved ? 'Saved!' : 'Save Webhook'}
                </button>
              </div>
            </div>
            {webhookSecret && (
              <div className="mt-4 p-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="fw-bold d-block small">Signing Secret</span>
                    <code className="text-dark">{webhookSecret}</code>
                  </div>
                  <button className="btn btn-sm btn-light border" onClick={() => copyToClipboard(webhookSecret, setCopied)}>
                    <Icon.Copy size={14} />
                  </button>
                </div>
                <div className="mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                  <Icon.Shield size={12} className="me-1"/>Use this secret to verify the <code>X-SchedPilot-Signature</code> header on your server.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Help / Resources ──────────────────────────────────────────────── */}
        <div className="row g-4 mt-2">
          {[
            { title: 'Documentation', desc: 'Full API reference and authentication guides.', icon: <Icon.Code size={20} />, link: 'https://docs.schedpilot.com' },
            { title: 'MCP Server', desc: 'Connect SchedPilot to your AI agents and IDEs.', icon: <Icon.Terminal size={20} />, link: '#' },
            { title: 'Support', desc: 'Join our developer community for help.', icon: <Icon.Slack size={20} />, link: '#' },
          ].map((item, idx) => (
            <div className="col-md-4" key={idx}>
              <div className="card h-100 border-0 shadow-sm p-2">
                <div className="card-body">
                  <div className="text-primary mb-2">{item.icon}</div>
                  <h6 className="fw-bold">{item.title}</h6>
                  <p className="text-muted mb-2">{item.desc}</p>
                  <a href={item.link} className="btn btn-link p-0 text-decoration-none fw-bold">Learn more →</a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── MODAL: New API Key ────────────────────────────────────────────── */}
        {generatedKey && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content border-01 shadow-lg">
                  <div className="modal-header bg-success text-white border-0 py-3">
                    <h6 className="modal-title fw-bold"><Icon.Shield size={18} className="me-2"/>API Key Created</h6>
                  </div>
                  <div className="modal-body p-4">
                    <p className="mb-3">Make sure to copy your API key now. <strong>You won't be able to see it again!</strong></p>
                    <div className="input-group mb-3">
                      <input type="text" className="form-control font-monospace bg-light" value={generatedKey} readOnly />
                      <button className="btn btn-dark" onClick={() => copyToClipboard(generatedKey, setCopied)}>
                        {copied ? 'Copied!' : <Icon.Copy size={16}/>}
                      </button>
                    </div>
                    <div className="alert alert-warning border-0 p-2 small mb-0">
                      <Icon.AlertTriangle size={14} className="me-2"/>
                      Treat this key like a password. Do not share it or commit it to public repositories.
                    </div>
                  </div>
                  <div className="modal-footer border-0">
                    <button type="button" className="btn btn-success w-100 fw-bold" onClick={() => setGeneratedKey(null)}>
                      I've saved it securely
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── MODAL: New OAuth App credentials ─────────────────────────────── */}
        {createdApp && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content border-01 shadow-lg">
                  <div className="modal-header border-0 py-3" style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff' }}>
                    <h6 className="modal-title fw-bold"><Icon.Key size={18} className="me-2"/>OAuth App Registered</h6>
                  </div>
                  <div className="modal-body p-4">
                    <div className="alert alert-warning border-0 p-3 mb-4">
                      <Icon.AlertTriangle size={15} className="me-2"/>
                      <strong>Copy the client secret now.</strong> It will <strong>not</strong> be shown again once you close this dialog.
                    </div>

                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-muted text-uppercase tracking-wide">Client ID</label>
                      <div className="input-group">
                        <input type="text" className="form-control font-monospace bg-light" value={createdApp.client_id} readOnly />
                        <button className="btn btn-outline-secondary" onClick={() => copyToClipboard(createdApp.client_id, setCopiedCid)}>
                          {copiedCid ? <><Icon.Check size={14} className="me-1"/>Copied</> : <Icon.Copy size={14}/>}
                        </button>
                      </div>
                      <div className="form-text">Use this as <code>client_id</code> in your authorization request.</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-muted text-uppercase tracking-wide">Client Secret <span className="badge bg-danger ms-1">Store securely</span></label>
                      <div className="input-group">
                        <input type="text" className="form-control font-monospace bg-light" value={createdApp.client_secret} readOnly />
                        <button className="btn btn-outline-secondary" onClick={() => copyToClipboard(createdApp.client_secret, setCopiedCs)}>
                          {copiedCs ? <><Icon.Check size={14} className="me-1"/>Copied</> : <Icon.Copy size={14}/>}
                        </button>
                      </div>
                      <div className="form-text">Keep this private. Do not expose it in client-side code or public repos.</div>
                    </div>

                    <hr />
                    <p className="small text-muted mb-2 fw-semibold">OAuth 2.0 Authorization Code Flow</p>
                    <ol className="small text-muted mb-0 ps-3">
                      <li>Redirect the user to <code>https://app.schedpilot.com/oauth/authorize?client_id=…&redirect_uri=…&response_type=code&state=…</code></li>
                      <li>User approves access → you receive a <code>code</code> at your redirect URI.</li>
                      <li>Exchange it: <code>POST https://api.schedpilot.com/developers/v1/oauth/token</code> with <code>client_id</code>, <code>client_secret</code>, <code>code</code>.</li>
                      <li>Use the returned <code>access_token</code> as <code>Authorization: Bearer sp_tok_…</code> on all <code>/developers/v1</code> requests.</li>
                    </ol>
                  </div>
                  <div className="modal-footer border-0 pt-0">
                    <button type="button" className="btn w-100 fw-bold text-white" style={{ background: '#4f46e5' }} onClick={() => setCreatedApp(null)}>
                      I've saved both credentials securely
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── MODAL: OAuth App Setup Instructions ──────────────────────────── */}
        {setupApp && (() => {
          const authUrl = `https://app.schedpilot.com/oauth/authorize?client_id=${setupApp.client_id}&redirect_uri=${encodeURIComponent(setupApp.redirect_uri)}&response_type=code&state=RANDOM_STATE_STRING`;
          return (
            <>
              <div className="modal-backdrop fade show"></div>
              <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ overflowY: 'auto' }}>
                <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
                  <div className="modal-content border-01 shadow-lg">

                    {/* Header */}
                    <div className="modal-header border-0 py-3" style={{ background: '#0f172a' }}>
                      <div>
                        <h6 className="modal-title fw-bold mb-0 text-white"><Icon.BookOpen size={17} className="me-2"/>Setup — {setupApp.name}</h6>
                        <div className="small mt-1 text-white" style={{ opacity: 0.6 }}>OAuth 2.0 Authorization Code flow</div>
                      </div>
                      <button type="button" className="btn-close btn-close-white ms-auto" onClick={() => { setSetupApp(null); setCopiedAuthUrl(false); }} />
                    </div>

                    <div className="modal-body p-4">

                      {/* Step 1 */}
                      <div className="d-flex gap-3 mb-4">
                        <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                             style={{ width: 28, height: 28, minWidth: 28, background: '#4f46e5', fontSize: '0.8rem' }}>1</div>
                        <div className="flex-grow-1">
                          <p className="fw-semibold mb-1">Redirect the user to the SchedPilot consent page</p>
                          <p className="text-muted mb-2">
                            Send the user to this URL. They'll see your app name and approve or deny access.
                            Replace <code>RANDOM_STATE_STRING</code> with a unique random value you generate per request (used to prevent CSRF).
                          </p>
                          <label className="form-label small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>Authorization URL</label>
                          <div className="input-group mb-1">
                            <input type="text" className="form-control font-monospace bg-light" style={{ fontSize: '0.75rem' }} value={authUrl} readOnly />
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => copyToClipboard(authUrl, setCopiedAuthUrl)}>
                              {copiedAuthUrl ? <><Icon.Check size={13} className="me-1"/>Copied</> : <Icon.Copy size={13}/>}
                            </button>
                          </div>
                          <div className="small text-muted">
                            <strong>On approval:</strong> SchedPilot redirects to <code>{setupApp.redirect_uri}?code=AUTH_CODE&state=…</code><br/>
                            <strong>On denial:</strong> <code>{setupApp.redirect_uri}?error=access_denied&state=…</code>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div className="d-flex gap-3 mb-4">
                        <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                             style={{ width: 28, height: 28, minWidth: 28, background: '#4f46e5', fontSize: '0.8rem' }}>2</div>
                        <div className="flex-grow-1">
                          <p className="fw-semibold mb-1">Exchange the code for an access token</p>
                          <p className="text-muted mb-2">
                            Make this request <strong>server-side only</strong> — never expose your <code>client_secret</code> in client-side code.
                          </p>
                          <label className="form-label small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>POST request</label>
                          <pre className="bg-light rounded p-3 small mb-2" style={{ fontSize: '0.75rem', overflowX: 'auto' }}>{`POST https://api.schedpilot.com/developers/v1/oauth/token
Content-Type: application/json

{
  "grant_type":    "authorization_code",
  "client_id":     "${setupApp.client_id}",
  "client_secret": "sp_cs_your_secret_here",
  "code":          "AUTH_CODE_FROM_REDIRECT"
}`}</pre>
                          <label className="form-label small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>Response</label>
                          <pre className="bg-light rounded p-3 small mb-0" style={{ fontSize: '0.75rem' }}>{`{
  "access_token": "sp_tok_xxxxxxxxxxxxxxxxxxxxxxxx",
  "token_type":   "bearer"
}`}</pre>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="d-flex gap-3 mb-4">
                        <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                             style={{ width: 28, height: 28, minWidth: 28, background: '#4f46e5', fontSize: '0.8rem' }}>3</div>
                        <div className="flex-grow-1">
                          <p className="fw-semibold mb-1">Call the SchedPilot API using the token</p>
                          <p className="text-muted mb-2">
                            Pass the access token in the <code>Authorization</code> header on every request.
                          </p>
                          <pre className="bg-light rounded p-3 small mb-0" style={{ fontSize: '0.75rem', overflowX: 'auto' }}>{`GET https://api.schedpilot.com/developers/v1/accounts
Authorization: Bearer sp_tok_xxxxxxxxxxxxxxxxxxxxxxxx`}</pre>
                        </div>
                      </div>

                      {/* Token lifecycle note */}
                      <div className="alert alert-light border small mb-0 p-3">
                        <Icon.Info size={14} className="me-2 text-primary"/>
                        <strong>Token lifecycle:</strong> tokens do not expire. Users can revoke them at any time from <strong>API Access → Active OAuth Tokens</strong>. If the API returns <code>401</code> or <code>403</code>, the user has likely revoked their token — restart the flow.
                      </div>

                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <a href="https://docs.schedpilot.com/docs/authentication/oauth" target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm me-auto">
                        <Icon.ExternalLink size={13} className="me-1"/>Full OAuth docs
                      </a>
                      <button type="button" className="btn btn-dark px-4" onClick={() => { setSetupApp(null); setCopiedAuthUrl(false); }}>
                        Close
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            </>
          );
        })()}

      </div>
    </MainStructure>
  );
}

export default ApiAccess;
